import { useDocumentTitle } from '@hooks/useDocumentTitle';
import { useWorkflowsByProgram } from '@hooks/useWorkflows';
import { useStore } from '@store/index';
import { XCircle } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { EmptyState } from '@/components/EmptyState';
import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';

import { DefaultCreateCasePage } from './DefaultCreateCasePage';
import { getCreatePageForWorkflow } from './registry';

/**
 * CreateCaseRouter - Dynamic component router for workflow-specific create case pages
 *
 * This component:
 * 1. Handles workflow initialization from URL search params
 * 2. Fetches the current workflow from the store
 * 3. Looks up a custom page component for that workflow type in the registry
 * 4. Renders the custom component if found, otherwise falls back to DefaultCreateCasePage
 *
 * This allows each workflow type to have a completely different create case page
 * with different layouts, components, and form behavior.
 */
export const CreateCaseRouter: React.FC = () => {
  const { progId } = useParams<{ progId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlWorkflowId = searchParams.get('workflow_id');
  const caseRecordId = searchParams.get('caseRecordId');

  // Track if URL contains invalid workflow ID
  const [invalidWorkflowId, setInvalidWorkflowId] = useState<string | null>(null);

  // Track if we've initialized from URL (prevents race condition on workflow switch)
  const hasInitializedFromUrl = useRef(false);

  const {
    getSelectedWorkflow,
    getSelectedProgram,
    getWorkflowsArray,
    setSelectedWorkflowId,
    selectedWorkflowId,
  } = useStore();
  const selectedProgram = getSelectedProgram();

  // Fetch workflows for the current program
  const { isLoading: workflowsLoading } = useWorkflowsByProgram(
    selectedProgram?.id || progId?.replace('prog-', '') || '',
  );

  // Initialize workflow from URL if valid (runs ONCE on mount)
  useEffect(() => {
    // Only run once - prevents reverting when user switches workflow
    if (hasInitializedFromUrl.current) {
      return;
    }

    const workflows = getWorkflowsArray();
    if (urlWorkflowId && workflows.length > 0) {
      if (workflows.some(w => w.id === urlWorkflowId)) {
        setSelectedWorkflowId(urlWorkflowId);
        hasInitializedFromUrl.current = true;
        setInvalidWorkflowId(null);
      } else {
        // Mark as invalid - show error instead of using default
        setInvalidWorkflowId(urlWorkflowId);
        hasInitializedFromUrl.current = true;
      }
    }
  }, [urlWorkflowId, getWorkflowsArray, setSelectedWorkflowId]);

  // Keep URL in sync when workflow changes (e.g., via header combobox)
  useEffect(() => {
    if (selectedWorkflowId && selectedWorkflowId !== urlWorkflowId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('workflow_id', selectedWorkflowId);
      navigate(`?${newParams.toString()}`, { replace: true });
    }
  }, [selectedWorkflowId, urlWorkflowId, searchParams, navigate]);

  const selectedWorkflow = getSelectedWorkflow();

  // Reflect the active program + workflow in the browser tab title (program first so
  // it survives tab truncation). Names only — no PHI.
  const docTitle =
    selectedProgram && selectedWorkflow
      ? `${selectedProgram.name} · ${selectedWorkflow.name} — New Case`
      : 'New Case';
  useDocumentTitle(docTitle);

  // Look up custom page component for this workflow type
  const CustomPageComponent = useMemo(() => {
    const workflowType = selectedWorkflow?.workflow_type;
    if (!workflowType) {
      return null;
    }
    return getCreatePageForWorkflow(workflowType);
  }, [selectedWorkflow?.workflow_type]);

  // Show error state for invalid workflow ID
  if (invalidWorkflowId) {
    return (
      <>
        <BreadcrumbPortal>
          <Breadcrumb>
            <Breadcrumb.Item to="../cases">Cases</Breadcrumb.Item>
            <Breadcrumb.Item>Create</Breadcrumb.Item>
          </Breadcrumb>
        </BreadcrumbPortal>
        <EmptyState
          title="Workflow Not Found"
          description={`The workflow "${invalidWorkflowId}" does not exist or you don't have access to it.`}
          icon={XCircle}
          iconClassName="text-muted-foreground"
          action={{
            label: 'Create Case',
            onClick: () => {
              setInvalidWorkflowId(null);
              navigate('../create-case', { replace: true });
            },
          }}
        />
      </>
    );
  }

  // Show loading state while workflows are being fetched
  if (workflowsLoading && !selectedWorkflow) {
    return (
      <>
        <BreadcrumbPortal>
          <Breadcrumb>
            <Breadcrumb.Item to="../cases">Cases</Breadcrumb.Item>
            <Breadcrumb.Item>Create</Breadcrumb.Item>
          </Breadcrumb>
        </BreadcrumbPortal>
        <div className="flex flex-col justify-center items-center h-full">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading workflow...</p>
        </div>
      </>
    );
  }

  // Use custom page if available, otherwise default
  const PageComponent = CustomPageComponent || DefaultCreateCasePage;

  // Key on caseRecordId so clearing it (Create a fresh case) remounts a clean form
  // rather than leaving the previously-linked case's state behind.
  return <PageComponent key={caseRecordId ?? 'new'} progId={progId} caseRecordId={caseRecordId} />;
};
